import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Separator } from '../../components/ui/separator';
import { Calendar as CalendarComponent } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ArticleAutocomplete } from '../../components/ui/article-autocomplete';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Html5Qrcode } from 'html5-qrcode';
import { useQrScanner } from '../../hooks/useQrScanner';
import {
  Plus, Search, Download, FileText, Settings,
  Wrench, Package, X, Check, AlertTriangle, Calendar, Clock,
  CheckCircle, PauseCircle, Upload, Save, CalendarDays, QrCode, Edit
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { ArticleQuantityDialog } from '../../components/ui/article-quantity-dialog';

const STADIU_OPTIONS = ['PLANIFICAT', 'IN LUCRU', 'IN ASTEPTARE PIESE', 'FINALIZAT', 'REPROGRAMAT', 'AMANAT', 'ANULAT'];
const STADIU_CONFIG = {
  PLANIFICAT: { label: 'Planificat', color: 'bg-blue-100 text-blue-700', rowBg: 'bg-blue-50/40', icon: Calendar },
  'IN LUCRU': { label: 'In Lucru', color: 'bg-amber-100 text-amber-700', rowBg: 'bg-amber-50/40', icon: Clock },
  'IN ASTEPTARE PIESE': { label: 'Asteptare piese', color: 'bg-purple-100 text-purple-700', rowBg: 'bg-purple-50/30', icon: Package },
  FINALIZAT: { label: 'Finalizat', color: 'bg-emerald-100 text-emerald-700', rowBg: 'bg-emerald-50/30', icon: CheckCircle },
  REPROGRAMAT: { label: 'Reprogramat', color: 'bg-orange-100 text-orange-700', rowBg: 'bg-orange-50/30', icon: CalendarDays },
  AMANAT: { label: 'Amanat', color: 'bg-red-100 text-red-700', rowBg: 'bg-red-50/30', icon: PauseCircle },
  ANULAT: { label: 'Anulat', color: 'bg-slate-100 text-slate-500', rowBg: 'bg-slate-50/30', icon: X },
};

const REZULTAT_OPTIONS = [
  'CONFORM', 'NECONFORM', 'PARTIAL CONFORM', 'NECESITA REVERIFICARE',
  'IN ASTEPTARE PIESE', 'FINALIZAT CU OBSERVATII', 'FARA DEFECTE CONSTATATE',
  'REPARATIE EFECTUATA', 'NECESITA INTERVENTIE SUPLIMENTARA'
];

const PMPlanPage = () => {
  const { hasRole } = useAuth();
  const [searchParams] = useSearchParams();

  // Data
  const [allEquipment, setAllEquipment] = useState([]);
  const [plans, setPlans] = useState([]);
  const [articlesList, setArticlesList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [stadiuFilter, setStadiuFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [drawerForm, setDrawerForm] = useState({
    perioada_programata: '', stadiu: 'PLANIFICAT', rezultat_mentenanta: '',
    consumabile: [], observatii: ''
  });
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [consumQty, setConsumQty] = useState('1');
  const [consumUm, setConsumUm] = useState('BUC');
  const [showQrScanner, setShowQrScanner] = useState(false);
  const qrScannerRef = useRef(null);
  const { start: startQrPM, stop: stopQrPM } = useQrScanner();
  const [equipmentHistory, setEquipmentHistory] = useState([]);
  const [pendingPMArticle, setPendingPMArticle] = useState(null);

  // Bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStadiu, setBulkStadiu] = useState('');
  const [bulkAction, setBulkAction] = useState('stadiu'); // stadiu, perioada, rezultat, observatii
  const [bulkPerioada, setBulkPerioada] = useState('');
  const [bulkRezultat, setBulkRezultat] = useState('');
  const [bulkObservatii, setBulkObservatii] = useState('');

  // PDF - direct download, signer configured via settings dialog
  const [pdfSignerUserId, setPdfSignerUserId] = useState('');
  const [pdfSignerName, setPdfSignerName] = useState('');
  const [pdfHeaderDate, setPdfHeaderDate] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showDocSettings, setShowDocSettings] = useState(false);

  // Excel
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  // PM Settings
  const [pmSettings, setPmSettings] = useState(null);

  // Cleanup QR scanner on unmount
  useEffect(() => {
    return () => { stopQrPM(); };
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [eqRes, plansRes, statsRes, articlesRes] = await Promise.all([
        api.get('/equipment'),
        api.get('/pm/plans', { params: { anul: yearFilter, is_active: true } }),
        api.get('/pm/stats', { params: { anul: yearFilter } }),
        api.get('/articles').catch(() => ({ data: [] }))
      ]);
      setAllEquipment(eqRes.data);
      setPlans(plansRes.data);
      setArticlesList(articlesRes.data.filter(a => a.is_active !== false));
      if (statsRes.data.available_years?.length > 0) {
        setAvailableYears(statsRes.data.available_years);
      }
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/pm/settings').then(r => {
      setPmSettings(r.data);
      if (r.data.pdf_signer_user_id) {
        setPdfSignerUserId(r.data.pdf_signer_user_id);
        setPdfSignerName(r.data.pdf_signer_name || '');
      }
      if (r.data.pdf_header_date) {
        setPdfHeaderDate(r.data.pdf_header_date);
      }
    }).catch(() => {});
    // Load users for PDF signer dropdown
    api.get('/users').then(r => {
      setAllUsers((r.data || []).filter(u => u.is_active !== false));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'pdf') handleGeneratePdf();
  }, []);

  // Merge equipment with plans - SERIE as primary key
  const mergedData = useMemo(() => {
    // Build plan map by serie (primary) and name+serie (fallback)
    const planBySerie = {};
    const planByNameSerie = {};
    const usedPlanIds = new Set();

    plans.forEach(p => {
      const serie = (p.serie || '').toUpperCase().trim();
      if (serie) planBySerie[serie] = p;
      const key = `${(p.denumire_utilaj || '').toUpperCase()}__${serie}`;
      planByNameSerie[key] = p;
    });

    const result = [];

    // First: map all equipment from nomenclator
    allEquipment.forEach(eq => {
      const serie = (eq.serial_number || '').toUpperCase().trim();
      const nameKey = `${(eq.name || '').toUpperCase()}__${serie}`;
      // Match by serie first, then by name+serie
      const plan = planBySerie[serie] || planByNameSerie[nameKey] || null;
      if (plan) usedPlanIds.add(plan.id);

      result.push({
        eq_id: eq.id,
        name: (eq.name || '').toUpperCase(),
        utilizare: (eq.utilizare || '').toUpperCase(),
        serial_number: serie,
        an_fabricatie: eq.an_fabricatie || '',
        putere_absorbita: (eq.putere_absorbita || '').toUpperCase(),
        locatie: plan ? (plan.locatie || '').toUpperCase() : (eq.plant_name || eq.location_name || '').toUpperCase(),
        category: eq.category_name || '',
        plan_id: plan?.id || null,
        perioada_programata: plan?.perioada_programata || '',
        stadiu: plan?.stadiu || '',
        rezultat_mentenanta: plan?.rezultat_mentenanta || '',
        consumabile: plan?.consumabile || [],
        observatii: plan?.observatii || '',
        is_planned: !!plan
      });
    });

    // Second: add orphan plans (plans without matching equipment in nomenclator)
    plans.forEach(p => {
      if (!usedPlanIds.has(p.id)) {
        result.push({
          eq_id: p.id,
          name: (p.denumire_utilaj || '').toUpperCase(),
          utilizare: (p.utilizare || '').toUpperCase(),
          serial_number: (p.serie || '').toUpperCase(),
          an_fabricatie: p.an_fabricatie || '',
          putere_absorbita: (p.putere_absorbita || '').toUpperCase(),
          locatie: (p.locatie || '').toUpperCase(),
          category: '',
          plan_id: p.id,
          perioada_programata: p.perioada_programata || '',
          stadiu: p.stadiu || '',
          rezultat_mentenanta: p.rezultat_mentenanta || '',
          consumabile: p.consumabile || [],
          observatii: p.observatii || '',
          is_planned: true
        });
      }
    });

    return result;
  }, [allEquipment, plans]);

  // Filter
  const filteredData = useMemo(() => {
    return mergedData.filter(item => {
      if (stadiuFilter === 'neplanificat') return !item.is_planned;
      if (stadiuFilter === 'planificat_all') return item.is_planned;
      if (stadiuFilter !== 'all' && item.stadiu !== stadiuFilter) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return item.name.toLowerCase().includes(s) ||
               item.utilizare.toLowerCase().includes(s) ||
               item.serial_number.toLowerCase().includes(s) ||
               item.locatie.toLowerCase().includes(s);
      }
      return true;
    });
  }, [mergedData, stadiuFilter, searchTerm]);

  // Stats
  const stats = useMemo(() => {
    const total = mergedData.length;
    const planned = mergedData.filter(i => i.is_planned).length;
    const unplanned = total - planned;
    const finalizat = mergedData.filter(i => i.stadiu === 'FINALIZAT').length;
    const inLucru = mergedData.filter(i => i.stadiu === 'IN LUCRU').length;
    return { total, planned, unplanned, finalizat, inLucru, pct: total > 0 ? Math.round((planned / total) * 100) : 0 };
  }, [mergedData]);

  // Open drawer for equipment
  const openDrawer = (item) => {
    setSelectedEquipment(item);
    // Parse interval dates if existing
    let intervalStart = '', intervalEnd = '';
    if (item.perioada_programata?.includes(' - ')) {
      const parts = item.perioada_programata.split(' - ');
      intervalStart = parts[0]?.trim() || '';
      intervalEnd = parts[1]?.trim() || '';
    }
    setDrawerForm({
      perioada_programata: item.perioada_programata || '',
      stadiu: item.stadiu || 'PLANIFICAT',
      rezultat_mentenanta: item.rezultat_mentenanta || '',
      consumabile: item.consumabile || [],
      observatii: item.observatii || '',
      _intervalStart: intervalStart,
      _intervalEnd: intervalEnd
    });
    setDrawerOpen(true);
    // Fetch history for this equipment
    if (item.serial_number) {
      api.get(`/pm/history/equipment/${encodeURIComponent(item.serial_number)}`)
        .then(res => setEquipmentHistory(res.data?.items || []))
        .catch(() => setEquipmentHistory([]));
    } else {
      setEquipmentHistory([]);
    }
  };

  // Save from drawer
  const handleDrawerSave = async () => {
    if (!selectedEquipment) return;
    if (!drawerForm.perioada_programata.trim()) {
      toast.error('Perioada programata este obligatorie');
      return;
    }
    setDrawerSaving(true);
    try {
      const payload = {
        anul: yearFilter,
        denumire_utilaj: selectedEquipment.name,
        utilizare: selectedEquipment.utilizare,
        serie: selectedEquipment.serial_number,
        an_fabricatie: String(selectedEquipment.an_fabricatie),
        putere_absorbita: selectedEquipment.putere_absorbita,
        locatie: selectedEquipment.locatie,
        perioada_programata: drawerForm.perioada_programata.toUpperCase(),
        stadiu: drawerForm.stadiu,
        rezultat_mentenanta: drawerForm.rezultat_mentenanta.toUpperCase(),
        consumabile: drawerForm.consumabile,
        observatii: drawerForm.observatii.toUpperCase()
      };

      if (selectedEquipment.plan_id) {
        await api.put(`/pm/plans/${selectedEquipment.plan_id}`, payload);
        toast.success('Planificare actualizata!');
      } else {
        await api.post('/pm/plans', payload);
        toast.success('Echipament planificat cu succes!');
      }
      setDrawerOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setDrawerSaving(false);
    }
  };

  // Remove plan
  const handleRemovePlan = async () => {
    if (!selectedEquipment?.plan_id) return;
    if (!window.confirm('Sigur doriti sa eliminati planificarea pentru acest echipament?')) return;
    try {
      await api.delete(`/pm/plans/${selectedEquipment.plan_id}`);
      toast.success('Planificare eliminata!');
      setDrawerOpen(false);
      fetchData();
    } catch {
      toast.error('Eroare la stergere');
    }
  };

  // Consumable handling in drawer
  const addConsumabil = (article) => {
    if (!article) return;
    setDrawerForm(prev => ({
      ...prev,
      consumabile: [...prev.consumabile, {
        denumire: (article.name || article).toString().toUpperCase().trim(),
        cantitate: Number(consumQty) || 1,
        um: (article.unit || consumUm).toUpperCase(),
        pret_unitar: 0,
        article_id: article.id || null,
        article_code: article.code || ''
      }]
    }));
    setConsumQty('1');
  };

  const addConsumabilManual = (name, unit) => {
    setDrawerForm(prev => ({
      ...prev,
      consumabile: [...prev.consumabile, {
        denumire: name.toUpperCase().trim(),
        cantitate: Number(consumQty) || 1,
        um: (unit || consumUm).toUpperCase(),
        pret_unitar: 0
      }]
    }));
    setConsumQty('1');
  };

  // QR Scanner for articles
  const startQrScanner = async () => {
    setShowQrScanner(true);
    setTimeout(async () => {
      try {
        await startQrPM('pm-qr-reader', async (decodedText) => {
          await stopQrPM();
          setShowQrScanner(false);
          let searchTerm = decodedText;
          const parts = decodedText.split(':');
          if (parts.length >= 2 && parts[0] === 'ART') {
            searchTerm = parts.slice(1).join(':');
          }
          try {
            const res = await api.get('/articles', { params: { search: searchTerm } });
            const found = res.data.find(a => a.code === searchTerm || a.name === searchTerm || a.id === searchTerm);
            const article = found || res.data[0];
            if (article) {
              setPendingPMArticle(article);
              toast.success(`Articol gasit: ${article.name}`);
            } else {
              toast.error(`Articol negasit pentru cod: ${searchTerm}`);
            }
          } catch {
            toast.error('Eroare la cautare articol');
          }
        }, { fps: 10, qrbox: { width: 200, height: 200 } });
      } catch (err) {
        toast.error('Nu se poate accesa camera');
        setShowQrScanner(false);
      }
    }, 300);
  };

  const stopQrScanner = async () => {
    await stopQrPM();
    setShowQrScanner(false);
  };

  const removeConsumabil = (idx) => {
    setDrawerForm(prev => ({
      ...prev,
      consumabile: prev.consumabile.filter((_, i) => i !== idx)
    }));
  };

  // Inline quick edit for perioada
  const handleInlinePerioada = async (item, newVal) => {
    if (!newVal.trim()) return;
    try {
      const payload = {
        anul: yearFilter,
        denumire_utilaj: item.name,
        utilizare: item.utilizare,
        serie: item.serial_number,
        an_fabricatie: String(item.an_fabricatie),
        putere_absorbita: item.putere_absorbita,
        locatie: item.locatie,
        perioada_programata: newVal.toUpperCase(),
        stadiu: item.stadiu || 'PLANIFICAT'
      };
      if (item.plan_id) {
        await api.put(`/pm/plans/${item.plan_id}`, payload);
      } else {
        await api.post('/pm/plans', payload);
      }
      toast.success('Perioada actualizata!');
      fetchData();
    } catch {
      toast.error('Eroare la salvare');
    }
  };

  // Inline stadiu change
  const handleInlineStadiu = async (item, newStadiu) => {
    if (!item.plan_id) return;
    try {
      await api.put(`/pm/plans/${item.plan_id}`, { stadiu: newStadiu });
      toast.success('Stadiu actualizat!');
      fetchData();
    } catch {
      toast.error('Eroare la salvare');
    }
  };

  // Bulk actions
  const toggleSelectAll = () => {
    const plannedIds = filteredData.filter(d => d.plan_id).map(d => d.eq_id);
    if (selectedIds.size === plannedIds.length && plannedIds.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(plannedIds));
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkStadiu = async () => {
    const items = filteredData.filter(d => selectedIds.has(d.eq_id) && d.plan_id);
    if (!items.length) { toast.error('Niciun echipament planificat selectat'); return; }

    if (bulkAction === 'stadiu') {
      if (!bulkStadiu) return;
      try {
        const ids = items.map(i => i.plan_id);
        await api.patch('/pm/plans/bulk-update-fields', { ids, fields: { stadiu: bulkStadiu } });
        toast.success(`Stadiu actualizat pentru ${items.length} echipamente!`);
        setSelectedIds(new Set()); setBulkStadiu(''); fetchData();
      } catch { toast.error('Eroare la actualizare in masa'); }
    } else if (bulkAction === 'perioada') {
      if (!bulkPerioada.trim()) return;
      try {
        const ids = items.map(i => i.plan_id);
        await api.patch('/pm/plans/bulk-update-fields', { ids, fields: { perioada_programata: bulkPerioada.toUpperCase() } });
        toast.success(`Perioada actualizata pentru ${items.length} echipamente!`);
        setSelectedIds(new Set()); setBulkPerioada(''); fetchData();
      } catch { toast.error('Eroare la actualizare in masa'); }
    } else if (bulkAction === 'rezultat') {
      if (!bulkRezultat) return;
      try {
        const ids = items.map(i => i.plan_id);
        await api.patch('/pm/plans/bulk-update-fields', { ids, fields: { rezultat_mentenanta: bulkRezultat } });
        toast.success(`Rezultat actualizat pentru ${items.length} echipamente!`);
        setSelectedIds(new Set()); setBulkRezultat(''); fetchData();
      } catch { toast.error('Eroare la actualizare in masa'); }
    } else if (bulkAction === 'observatii') {
      try {
        const ids = items.map(i => i.plan_id);
        await api.patch('/pm/plans/bulk-update-fields', { ids, fields: { observatii: bulkObservatii.toUpperCase() } });
        toast.success(`Observatii actualizate pentru ${items.length} echipamente!`);
        setSelectedIds(new Set()); setBulkObservatii(''); fetchData();
      } catch { toast.error('Eroare la actualizare in masa'); }
    }
  };

  const handleBulkDelete = async () => {
    const items = filteredData.filter(d => selectedIds.has(d.eq_id) && d.plan_id);
    if (!items.length) { toast.error('Niciun echipament planificat selectat'); return; }
    if (!window.confirm(`Sigur doriti sa stergeti planificarea pentru ${items.length} echipamente?`)) return;
    try {
      await Promise.all(items.map(item => api.delete(`/pm/plans/${item.plan_id}`)));
      toast.success(`${items.length} planificari sterse!`);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Eroare la stergere in masa'); }
  };

  // PDF Generation - direct download, no dialog
  const handleGeneratePdf = async () => {
    if (pdfGenerating) return;
    try {
      setPdfGenerating(true);
      let signatureData = null;

      // Fetch digital signature for configured signer
      if (pdfSignerUserId) {
        try {
          const sigRes = await api.get(`/signatures/${pdfSignerUserId}`);
          if (sigRes.data?.signature_data) {
            signatureData = sigRes.data.signature_data;
          }
        } catch {
          // No signature found - will use line instead
        }
      }

      const signerRole = pmSettings?.responsabil_functie || 'Responsabil Mentenanta utilajelor';
      const payload = {
        signature: signatureData,
        signer_name: pdfSignerName,
        signer_role: signerRole,
        anul: yearFilter,
        header_date: pdfHeaderDate || null
      };
      const response = await api.post('/pm/generate-pdf', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Plan_Mentenanta_${yearFilter}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descarcat!');
    } catch {
      toast.error('Eroare la generare PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  // Save document settings to PM settings
  const handleSignerChange = (userId) => {
    if (userId === '__none__') {
      setPdfSignerUserId('');
      setPdfSignerName('');
    } else {
      const u = allUsers.find(u => u.id === userId);
      setPdfSignerUserId(userId);
      setPdfSignerName(u?.full_name?.toUpperCase() || '');
    }
  };

  const handleSaveDocSettings = async () => {
    try {
      await api.post('/pm/settings', {
        ...pmSettings,
        pdf_signer_user_id: pdfSignerUserId,
        pdf_signer_name: pdfSignerName,
        pdf_header_date: pdfHeaderDate,
      });
      toast.success('Setari document salvate!');
      setShowDocSettings(false);
    } catch { toast.error('Eroare la salvare'); }
  };

  // Excel Export
  const handleExportExcel = async () => {
    try {
      const response = await api.post('/pm/export-excel', { anul: yearFilter }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Plan_Mentenanta_${yearFilter}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel realizat!');
    } catch {
      toast.error('Eroare la export Excel');
    }
  };

  // Excel Import
  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);
      if (rows.length === 0) { toast.error('Fisierul nu contine date'); setImporting(false); return; }
      let imported = 0, errors = 0;
      for (const row of rows) {
        const denumire = (row['Denumire utilaj'] || row['denumire_utilaj'] || '').toString().trim().toUpperCase();
        if (!denumire) { errors++; continue; }
        try {
          await api.post('/pm/plans', {
            anul: yearFilter, denumire_utilaj: denumire,
            utilizare: (row['Utilizare'] || '').toString().toUpperCase(),
            serie: (row['Serie'] || '').toString().toUpperCase(),
            an_fabricatie: (row['An fabricatie'] || '').toString(),
            putere_absorbita: (row['Putere absorbita'] || '').toString().toUpperCase(),
            locatie: (row['Locatie'] || '').toString().toUpperCase(),
            perioada_programata: (row['Perioada programata'] || '').toString().toUpperCase(),
            stadiu: (row['Stadiu'] || 'PLANIFICAT').toString().toUpperCase(),
            rezultat_mentenanta: (row['Rezultatul mentenantei'] || '').toString().toUpperCase(),
          });
          imported++;
        } catch { errors++; }
      }
      toast.success(`Import: ${imported} reusit, ${errors} erori`);
      fetchData();
    } catch {
      toast.error('Eroare la citire fisier Excel');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="pm-plan-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pm-plan-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Plan Mentenanta {yearFilter}</h1>
          <p className="text-slate-500 text-sm mt-0.5">Planificator mentenanta preventiva - toate echipamentele</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(yearFilter)} onValueChange={v => setYearFilter(Number(v))}>
            <SelectTrigger className="w-28 h-9" data-testid="pm-plan-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-9" onClick={() => setShowDocSettings(true)} data-testid="pm-doc-settings-btn">
            <Settings className="w-4 h-4 mr-1" />Setari document
          </Button>
          <Button variant="outline" className="h-9" onClick={handleGeneratePdf} disabled={pdfGenerating} data-testid="pm-pdf-btn">
            <FileText className="w-4 h-4 mr-1" />{pdfGenerating ? 'Se genereaza...' : 'PDF'}
          </Button>
          <Button variant="outline" className="h-9" onClick={handleExportExcel} data-testid="pm-excel-export-btn">
            <Download className="w-4 h-4 mr-1" />Excel
          </Button>
          {hasRole('admin', 'manager') && (
            <>
              <Button variant="outline" className="h-9" onClick={() => fileInputRef.current?.click()}
                      disabled={importing} data-testid="pm-excel-import-btn">
                <Upload className="w-4 h-4 mr-1" />{importing ? 'Import...' : 'Import'}
              </Button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportExcel} />
            </>
          )}
        </div>
      </div>

      {/* Progress Bar + Stats */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">
              Progres planificare {yearFilter}
            </span>
            <span className="text-sm text-slate-500">
              <span className="font-bold text-[#0052CC]">{stats.planned}</span> din {stats.total} echipamente planificate
            </span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${stats.pct}%`,
                background: stats.pct === 100
                  ? 'linear-gradient(90deg, #10b981, #059669)'
                  : 'linear-gradient(90deg, #3b82f6, #0052CC)'
              }}
            />
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
              <span className="text-slate-600">{stats.unplanned} neplanificate</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-400"></span>
              <span className="text-slate-600">{stats.planned - stats.finalizat - stats.inLucru} planificate</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
              <span className="text-slate-600">{stats.inLucru} in lucru</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
              <span className="text-slate-600">{stats.finalizat} finalizate</span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cauta echipament..." value={searchTerm}
                 onChange={e => setSearchTerm(e.target.value)} className="pl-10 h-9" data-testid="pm-plan-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: 'all', label: 'Toate', count: mergedData.length },
            { key: 'neplanificat', label: 'Neplanificate', count: stats.unplanned, dot: 'bg-red-400' },
            { key: 'planificat_all', label: 'Planificate', count: stats.planned, dot: 'bg-blue-400' },
            { key: 'IN LUCRU', label: 'In Lucru', count: stats.inLucru, dot: 'bg-amber-400' },
            { key: 'FINALIZAT', label: 'Finalizate', count: stats.finalizat, dot: 'bg-emerald-400' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStadiuFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                stadiuFilter === f.key
                  ? 'bg-[#0052CC] text-white border-[#0052CC]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`pm-filter-${f.key}`}
            >
              {f.dot && stadiuFilter !== f.key && <span className={`inline-block w-2 h-2 rounded-full ${f.dot} mr-1.5`}></span>}
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3" data-testid="pm-bulk-toolbar">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-bold text-blue-700">{selectedIds.size} selectate</span>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <Select value={bulkAction} onValueChange={setBulkAction}>
                <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="pm-bulk-action-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stadiu">Schimba Stadiu</SelectItem>
                  <SelectItem value="perioada">Schimba Perioada</SelectItem>
                  <SelectItem value="rezultat">Schimba Rezultat</SelectItem>
                  <SelectItem value="observatii">Schimba Observatii</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bulkAction === 'stadiu' && (
              <div className="flex items-center gap-2">
                <Select value={bulkStadiu} onValueChange={setBulkStadiu}>
                  <SelectTrigger className="h-8 w-44 text-xs bg-white"><SelectValue placeholder="Selecteaza stadiu..." /></SelectTrigger>
                  <SelectContent>
                    {STADIU_OPTIONS.map(s => <SelectItem key={s} value={s}>{STADIU_CONFIG[s]?.label || s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8 bg-[#0052CC]" onClick={handleBulkStadiu} disabled={!bulkStadiu} data-testid="pm-bulk-apply-btn">
                  <Check className="w-3 h-3 mr-1" />Aplica
                </Button>
              </div>
            )}

            {bulkAction === 'perioada' && (
              <div className="flex items-center gap-2">
                <Input value={bulkPerioada} onChange={e => setBulkPerioada(e.target.value)}
                  placeholder="ex: IANUARIE, SAPTAMANAL, 01.03-15.03..." className="h-8 w-56 text-xs bg-white" data-testid="pm-bulk-perioada" />
                <Button size="sm" className="h-8 bg-[#0052CC]" onClick={handleBulkStadiu} disabled={!bulkPerioada.trim()} data-testid="pm-bulk-apply-btn">
                  <Check className="w-3 h-3 mr-1" />Aplica
                </Button>
              </div>
            )}

            {bulkAction === 'rezultat' && (
              <div className="flex items-center gap-2">
                <Select value={bulkRezultat} onValueChange={setBulkRezultat}>
                  <SelectTrigger className="h-8 w-52 text-xs bg-white"><SelectValue placeholder="Selecteaza rezultat..." /></SelectTrigger>
                  <SelectContent>
                    {REZULTAT_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8 bg-[#0052CC]" onClick={handleBulkStadiu} disabled={!bulkRezultat} data-testid="pm-bulk-apply-btn">
                  <Check className="w-3 h-3 mr-1" />Aplica
                </Button>
              </div>
            )}

            {bulkAction === 'observatii' && (
              <div className="flex items-center gap-2">
                <Input value={bulkObservatii} onChange={e => setBulkObservatii(e.target.value)}
                  placeholder="Text observatii..." className="h-8 w-56 text-xs bg-white" data-testid="pm-bulk-observatii" />
                <Button size="sm" className="h-8 bg-[#0052CC]" onClick={handleBulkStadiu} data-testid="pm-bulk-apply-btn">
                  <Check className="w-3 h-3 mr-1" />Aplica
                </Button>
              </div>
            )}

            <Separator orientation="vertical" className="h-6" />
            <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" onClick={handleBulkDelete} data-testid="pm-bulk-delete-btn">
              <X className="w-3 h-3 mr-1" />Sterge planificari
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => setSelectedIds(new Set())}>
              Deselecteaza
            </Button>
          </div>
        </div>
      )}

      {/* Equipment Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="pm-plan-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="text-center px-2 py-2.5 w-8">
                  <input type="checkbox" className="rounded cursor-pointer"
                    checked={selectedIds.size > 0 && selectedIds.size === filteredData.filter(d => d.plan_id).length}
                    onChange={toggleSelectAll} data-testid="pm-select-all" />
                </th>
                <th className="text-left px-3 py-2.5 font-medium text-xs w-8">Nr.</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs">Denumire utilaj</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs">Utilizare</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs w-24">Serie</th>
                <th className="text-center px-3 py-2.5 font-medium text-xs w-16">An</th>
                <th className="text-center px-3 py-2.5 font-medium text-xs w-20">Putere</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs w-24">Locatie</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs w-32">Perioada</th>
                <th className="text-center px-3 py-2.5 font-medium text-xs w-28">Stadiu</th>
                <th className="text-center px-3 py-2.5 font-medium text-xs w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, idx) => {
                const cfg = STADIU_CONFIG[item.stadiu];
                const rowClass = item.is_planned
                  ? (cfg?.rowBg || 'bg-white')
                  : 'bg-white';
                return (
                  <tr
                    key={`${item.eq_id}-${idx}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${rowClass}`}
                    onClick={() => openDrawer(item)}
                    data-testid={`pm-row-${item.eq_id}`}
                  >
                    <td className="text-center px-2 py-2" onClick={e => e.stopPropagation()}>
                      {item.plan_id ? (
                        <input type="checkbox" className="rounded cursor-pointer"
                          checked={selectedIds.has(item.eq_id)}
                          onChange={() => toggleSelectItem(item.eq_id)}
                          data-testid={`pm-check-${item.eq_id}`} />
                      ) : <span className="text-slate-200">-</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-400 text-xs">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-900">{item.name}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{item.utilizare}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs font-mono">{item.serial_number}</td>
                    <td className="px-3 py-2 text-center text-slate-500 text-xs">{item.an_fabricatie}</td>
                    <td className="px-3 py-2 text-center text-slate-500 text-xs">{item.putere_absorbita}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{item.locatie}</td>
                    <td className="px-3 py-2">
                      {item.perioada_programata ? (
                        <span className="text-sm font-medium text-slate-700">{item.perioada_programata}</span>
                      ) : (
                        <span className="text-xs text-red-400 italic flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />NEPLANIFICAT
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                      {item.is_planned ? (
                        <div className="relative inline-flex items-center">
                          <select
                            value={item.stadiu}
                            onChange={e => handleInlineStadiu(item, e.target.value)}
                            className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 cursor-pointer appearance-none pr-7 ${cfg?.color || 'bg-slate-100'} ${
                              item.stadiu === 'FINALIZAT' ? 'border-emerald-300' : item.stadiu === 'AMANAT' ? 'border-red-300' : 'border-transparent'
                            } hover:ring-2 hover:ring-blue-300 transition-all`}
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                            data-testid={`pm-inline-stadiu-${item.eq_id}`}
                          >
                            {STADIU_OPTIONS.map(s => (
                              <option key={s} value={s}>{STADIU_CONFIG[s]?.label || s}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => openDrawer(item)} className="p-1.5 rounded-md hover:bg-blue-50 transition-colors group" title="Editeaza planificarea">
                        <Edit className="w-4 h-4 text-slate-300 group-hover:text-blue-600 transition-colors" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-slate-400">
                    <Wrench className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Nu s-au gasit echipamente</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 bg-slate-50 border-t text-xs text-slate-500">
          {filteredData.length} echipamente afisate din {mergedData.length} total
        </div>
      </div>

      {/* Side Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
          {selectedEquipment && (
            <div className="flex flex-col h-full">
              {/* Drawer Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-5 text-white">
                <SheetHeader>
                  <SheetTitle className="text-white text-lg flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    {selectedEquipment.name}
                  </SheetTitle>
                  <SheetDescription className="text-slate-300 text-sm">
                    {selectedEquipment.utilizare || 'Echipament industrial'}
                  </SheetDescription>
                </SheetHeader>
              </div>

              {/* Equipment Info */}
              <div className="p-5 border-b bg-slate-50/50">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400 text-xs">Serie</span>
                    <p className="font-medium text-slate-700">{selectedEquipment.serial_number || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs">An fabricatie</span>
                    <p className="font-medium text-slate-700">{selectedEquipment.an_fabricatie || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs">Putere absorbita</span>
                    <p className="font-medium text-slate-700">{selectedEquipment.putere_absorbita || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400 text-xs">Locatie</span>
                    <p className="font-medium text-slate-700">{selectedEquipment.locatie || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Planning Form */}
              <div className="p-5 space-y-4 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-[#0052CC]" />
                  <span className="font-semibold text-slate-800 text-sm">Planificare mentenanta {yearFilter}</span>
                </div>

                <div className="space-y-3">
                  <Label className="text-xs font-medium">Perioada programata *</Label>
                  {/* Period type selector */}
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { key: 'saptamanal', label: 'Saptamanal', icon: '↻' },
                      { key: 'lunar', label: 'Lunar', icon: '▦' },
                      { key: 'interval', label: 'Interval zile', icon: '↔' },
                      { key: 'data_fixa', label: 'Data fixa', icon: '◉' },
                    ].map(t => {
                      const currentType = drawerForm.perioada_programata === 'SAPTAMANAL' ? 'saptamanal'
                        : ['IANUARIE','FEBRUARIE','MARTIE','APRILIE','MAI','IUNIE','IULIE','AUGUST','SEPTEMBRIE','OCTOMBRIE','NOIEMBRIE','DECEMBRIE'].includes(drawerForm.perioada_programata) ? 'lunar'
                        : drawerForm.perioada_programata?.includes(' - ') || drawerForm.perioada_programata?.includes('-') ? 'interval'
                        : drawerForm.perioada_programata ? 'data_fixa' : '';
                      return (
                        <button key={t.key} type="button"
                          onClick={() => {
                            if (t.key === 'saptamanal') setDrawerForm(p => ({ ...p, perioada_programata: 'SAPTAMANAL' }));
                            else if (t.key === 'lunar') setDrawerForm(p => ({ ...p, perioada_programata: '', _periodType: 'lunar' }));
                            else if (t.key === 'interval') setDrawerForm(p => ({ ...p, perioada_programata: '', _periodType: 'interval' }));
                            else setDrawerForm(p => ({ ...p, perioada_programata: '', _periodType: 'data_fixa' }));
                          }}
                          className={`px-2 py-2 rounded-lg text-[11px] font-medium border transition-all text-center ${
                            (t.key === 'saptamanal' && drawerForm.perioada_programata === 'SAPTAMANAL') ||
                            (t.key === currentType && currentType !== 'saptamanal')
                              ? 'bg-[#0052CC] text-white border-[#0052CC] shadow-sm'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}>
                          <span className="block text-sm mb-0.5">{t.icon}</span>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Lunar: month selector */}
                  {drawerForm.perioada_programata !== 'SAPTAMANAL' && (
                    (() => {
                      const months = ['IANUARIE','FEBRUARIE','MARTIE','APRILIE','MAI','IUNIE','IULIE','AUGUST','SEPTEMBRIE','OCTOMBRIE','NOIEMBRIE','DECEMBRIE'];
                      const isMonth = months.includes(drawerForm.perioada_programata);
                      const isInterval = drawerForm.perioada_programata?.includes(' - ') || drawerForm.perioada_programata?.includes('-');
                      const periodType = drawerForm._periodType || (isMonth ? 'lunar' : isInterval ? 'interval' : drawerForm.perioada_programata ? 'data_fixa' : 'lunar');

                      if (periodType === 'lunar') return (
                        <div className="grid grid-cols-4 gap-1.5">
                          {months.map(m => (
                            <button key={m} type="button"
                              onClick={() => setDrawerForm(p => ({ ...p, perioada_programata: m, _periodType: 'lunar' }))}
                              className={`px-1 py-1.5 rounded text-[10px] font-medium border transition-all ${
                                drawerForm.perioada_programata === m
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'
                              }`}>
                              {m.substring(0, 3)}
                            </button>
                          ))}
                        </div>
                      );

                      if (periodType === 'interval') return (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-slate-500 mb-1 block">Data inceput</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-full h-9 justify-start text-left text-xs font-normal">
                                    <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                                    {drawerForm._intervalStart || <span className="text-slate-400">Selecteaza...</span>}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single" locale={ro} numberOfMonths={1} className="rounded-md"
                                    onSelect={(val) => {
                                      if (val) {
                                        const formatted = format(val, 'dd.MM');
                                        const end = drawerForm._intervalEnd || '';
                                        setDrawerForm(p => ({
                                          ...p,
                                          _intervalStart: formatted,
                                          _periodType: 'interval',
                                          perioada_programata: end ? `${formatted} - ${end}` : formatted
                                        }));
                                      }
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500 mb-1 block">Data sfarsit</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-full h-9 justify-start text-left text-xs font-normal">
                                    <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                                    {drawerForm._intervalEnd || <span className="text-slate-400">Selecteaza...</span>}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single" locale={ro} numberOfMonths={1} className="rounded-md"
                                    onSelect={(val) => {
                                      if (val) {
                                        const formatted = format(val, 'dd.MM');
                                        const start = drawerForm._intervalStart || '';
                                        setDrawerForm(p => ({
                                          ...p,
                                          _intervalEnd: formatted,
                                          _periodType: 'interval',
                                          perioada_programata: start ? `${start} - ${formatted}` : formatted
                                        }));
                                      }
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                          {drawerForm.perioada_programata && (
                            <div className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded font-medium text-center">
                              {drawerForm.perioada_programata}
                            </div>
                          )}
                        </div>
                      );

                      if (periodType === 'data_fixa') return (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full h-10 justify-start text-left font-normal"
                                    data-testid="pm-drawer-perioada">
                              <CalendarDays className="w-4 h-4 mr-2 text-slate-400" />
                              {drawerForm.perioada_programata || <span className="text-slate-400">Selecteaza data din calendar...</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <div className="p-3 border-b">
                              <Input
                                value={drawerForm.perioada_programata}
                                onChange={e => setDrawerForm(p => ({ ...p, perioada_programata: e.target.value.toUpperCase() }))}
                                placeholder="ex: 15.03.2026"
                                className="h-9 text-sm"
                              />
                            </div>
                            <CalendarComponent
                              mode="single"
                              locale={ro}
                              onSelect={(val) => {
                                if (val) {
                                  setDrawerForm(p => ({ ...p, perioada_programata: format(val, 'dd.MM.yyyy') }));
                                }
                              }}
                              numberOfMonths={1}
                              className="rounded-md"
                            />
                          </PopoverContent>
                        </Popover>
                      );

                      return null;
                    })()
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Stadiu</Label>
                    <Select value={drawerForm.stadiu} onValueChange={v => setDrawerForm(p => ({ ...p, stadiu: v }))}>
                      <SelectTrigger className="h-10" data-testid="pm-drawer-stadiu"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STADIU_OPTIONS.map(s => <SelectItem key={s} value={s}>{STADIU_CONFIG[s]?.label || s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Rezultat mentenanta</Label>
                    <Select value={drawerForm.rezultat_mentenanta}
                            onValueChange={v => setDrawerForm(p => ({ ...p, rezultat_mentenanta: v }))}>
                      <SelectTrigger className="h-10"><SelectValue placeholder="Selecteaza rezultat" /></SelectTrigger>
                      <SelectContent>
                        {REZULTAT_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Consumables with ArticleAutocomplete + QR */}
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-[#0052CC]" />Consumabile utilizate
                    </Label>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                            onClick={startQrScanner} data-testid="pm-drawer-qr-btn">
                      <QrCode className="w-3 h-3 mr-1" />Scan QR
                    </Button>
                  </div>
                  {(drawerForm.consumabile || []).length > 0 && (
                    <div className="space-y-1 mb-3">
                      {drawerForm.consumabile.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 px-3 py-2 rounded-lg border">
                          <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          <span className="flex-1 font-medium">{c.denumire}</span>
                          {c.article_code && <span className="text-slate-400">({c.article_code})</span>}
                          <span className="text-slate-500">{c.cantitate} {c.um}</span>
                          <button onClick={() => removeConsumabil(i)} className="text-red-400 hover:text-red-600">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* QR Scanner Modal */}
                  {showQrScanner && (
                    <div className="mb-3 border rounded-lg overflow-hidden bg-black">
                      <div className="flex items-center justify-between p-2 bg-slate-800">
                        <span className="text-xs text-white">Scaneaza codul QR al articolului</span>
                        <Button variant="ghost" size="sm" className="h-6 text-white" onClick={stopQrScanner}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                      <div id="pm-qr-reader" style={{ width: '100%', minHeight: 200 }}></div>
                    </div>
                  )}
                  {/* Article search */}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <ArticleAutocomplete
                        articles={articlesList}
                        onSelect={(article) => setPendingPMArticle(article)}
                        placeholder="Cauta articol / consumabil..."
                        className="text-xs"
                      />
                    </div>
                    <Input type="number" value={consumQty} onChange={e => setConsumQty(e.target.value)}
                           className="w-16 h-10 text-xs" min="1" placeholder="Cant." />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Observatii</Label>
                  <Textarea
                    value={drawerForm.observatii}
                    onChange={e => setDrawerForm(p => ({ ...p, observatii: e.target.value.toUpperCase() }))}
                    placeholder="Observatii suplimentare..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Equipment History */}
              {equipmentHistory.length > 0 && (
                <div className="px-4 pb-3">
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-800 px-3 py-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-300" />
                      <span className="text-xs font-bold text-white tracking-wider">ISTORIC MENTENANTA ({equipmentHistory.length})</span>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {equipmentHistory.map((h, i) => (
                        <div key={h.id || i} className="px-3 py-2 border-b last:border-b-0 bg-slate-50 hover:bg-slate-100">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-700">{h.anul}</span>
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                              {h.rezultat_mentenanta || 'FINALIZAT'}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            Perioada: {h.perioada_programata || '-'} | De: {h.finalizat_de_name || '-'}
                          </p>
                          {h.finalizat_la && (
                            <p className="text-[10px] text-slate-400">
                              {new Date(h.finalizat_la).toLocaleDateString('ro-RO')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Drawer Footer */}
              <div className="p-4 border-t bg-white sticky bottom-0 space-y-2">
                <Button
                  className="w-full h-11 bg-[#0052CC] hover:bg-[#003d99] text-sm"
                  onClick={handleDrawerSave}
                  disabled={drawerSaving}
                  data-testid="pm-drawer-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {drawerSaving ? 'Se salveaza...' : (selectedEquipment.plan_id ? 'ACTUALIZEAZA PLANIFICAREA' : 'PLANIFICA MENTENANTA')}
                </Button>
                {selectedEquipment.plan_id && hasRole('admin', 'manager') && (
                  <Button variant="outline" className="w-full h-9 text-red-600 text-xs" onClick={handleRemovePlan}>
                    <X className="w-3 h-3 mr-1" />Elimina planificarea
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Setari Document Dialog */}
      <Dialog open={showDocSettings} onOpenChange={setShowDocSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-[#0052CC]" />Setari document PDF</DialogTitle>
            <p className="text-sm text-muted-foreground">Configureaza semnatura si data din antet pentru export PDF</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">Semnatura pe ultima pagina</Label>
              <Select value={pdfSignerUserId || '__none__'} onValueChange={handleSignerChange}>
                <SelectTrigger className="h-10" data-testid="pm-pdf-signer-select"><SelectValue placeholder="Selecteaza utilizatorul..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Fara semnatura</SelectItem>
                  {allUsers.filter(u => ['admin', 'manager', 'technician'].includes(u.role)).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500">Semnatura digitala va aparea automat pe ultima pagina PDF</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">Data din antet document</Label>
              <Input value={pdfHeaderDate} onChange={e => setPdfHeaderDate(e.target.value)}
                placeholder="ex: 15.01.2026 (gol = data curenta)" className="h-10" data-testid="pm-pdf-header-date" />
              <p className="text-[10px] text-slate-500">Daca e gol, se foloseste data curenta la generare</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDocSettings(false)}>Inchide</Button>
            <Button className="bg-[#0052CC]" onClick={handleSaveDocSettings} data-testid="pm-doc-settings-save">
              <Save className="w-4 h-4 mr-1" />Salveaza
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ArticleQuantityDialog
        article={pendingPMArticle}
        onConfirm={(article, qty) => {
          setConsumQty(String(qty));
          addConsumabil(article);
          setPendingPMArticle(null);
        }}
        onCancel={() => setPendingPMArticle(null)}
      />
    </div>
  );
};

export default PMPlanPage;
